import { canEdit, RoleLevels } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { useAppSelector } from '@src/redux';
import { roleSelector } from '@src/redux/user';

/**
 * Whether this user may edit one saved chart or dashboard, and the role number behind that answer.
 *
 * The frontend works permission out for itself rather than asking the server for a flag — but it runs
 * the SAME `canEdit` the server runs, from `bpm-types`, so the two sides cannot drift into quietly
 * disagreeing. The server remains the authority: every write is refused there regardless.
 *
 * Deliberately no `canView`. Read permission is enforced only on the server — the lists are filtered
 * in SQL and /getChart refuses to send a restricted chart — so a `canView` call here could never be
 * reached with a level that fails. Do not add one for symmetry.
 */
export const useRecordPermissions = (
  record?: RoleLevels
): { isResolved: boolean; canEdit: boolean; userRole: number } => {
  const userRole = useAppSelector(roleSelector);

  // `isResolved` tracks THE RECORD, not the session. While the envelope is loading, `record` is
  // undefined — and without this guard the caller would show a "View only" badge on every editable
  // chart, on every page load, for the length of the /getChart round trip.
  if (!record) {
    return { isResolved: false, canEdit: false, userRole };
  }

  const { viewRoleRequired, editRoleRequired } = record;

  // Treat the two levels as ONE unit. A record is governed when it carries both; if either is
  // missing it is ungoverned, which means EDITABLE — that is what lets a user save the first
  // dashboard at a company that has none. Never fall back to the edit level alone when the view
  // level is absent: that rebuilds the exact hole the ceiling closes, one layer above the server.
  //
  // Destructure first, then compare against undefined. TypeScript narrows both fields that way, so
  // the object handed to `canEdit` is two plain numbers and needs no cast.
  return {
    isResolved: true,
    canEdit:
      viewRoleRequired === undefined || editRoleRequired === undefined
        ? true
        : canEdit(userRole, { viewRoleRequired, editRoleRequired }),
    userRole
  };
};
